import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getCountryContacts, createCountryContact, updateCountryContact,
  deleteCountryContact, uploadContactAvatar,
} from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../../components/ui/dialog';
import { Plus, Edit, Trash2, MapPin, Mail, Phone, Image as ImageIcon, Globe, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { COUNTRIES } from '../../utils/enrollmentOptions';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const empty = () => ({
  country: '', name: '', position: '', email: '', phone: '',
  whatsapp: '', address: '', avatar_url: '', notes: '', order: 0,
});

const ManageCountryContacts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const avatarRef = useRef();

  useEffect(() => {
    if (user && !['superadmin', 'admin'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const r = await getCountryContacts();
      setItems(r.data || []);
    } catch {
      toast.error('Error al cargar contactos');
    } finally {
      setLoading(false);
    }
  };

  const grouped = useMemo(() => {
    const m = {};
    items.forEach(c => { (m[c.country] = m[c.country] || []).push(c); });
    return m;
  }, [items]);

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const resetForm = () => {
    setForm(empty()); setEditing(null); setAvatarFile(null); setAvatarPreview(null);
  };

  const handleEdit = (c) => {
    setEditing(c);
    setForm({
      country: c.country || '', name: c.name || '', position: c.position || '',
      email: c.email || '', phone: c.phone || '', whatsapp: c.whatsapp || '',
      address: c.address || '', avatar_url: c.avatar_url || '',
      notes: c.notes || '', order: c.order || 0,
    });
    setAvatarFile(null);
    setAvatarPreview(c.avatar_url
      ? (c.avatar_url.startsWith('/api') ? `${API_BASE}${c.avatar_url}` : c.avatar_url)
      : null);
    setDialogOpen(true);
  };

  const handleAvatarPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAvatarFile(f);
    setAvatarPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.country || !form.name) {
      toast.error('País y nombre son obligatorios');
      return;
    }
    setSaving(true);
    try {
      let avatarUrl = form.avatar_url;
      if (avatarFile) {
        const fd = new FormData();
        fd.append('file', avatarFile);
        const r = await uploadContactAvatar(fd);
        avatarUrl = r.data.url;
      }
      const payload = { ...form, avatar_url: avatarUrl || null, order: Number(form.order) || 0 };
      if (editing) {
        await updateCountryContact(editing.id, payload);
        toast.success('Contacto actualizado');
      } else {
        await createCountryContact(payload);
        toast.success('Contacto creado');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este contacto?')) return;
    try {
      await deleteCountryContact(id);
      toast.success('Contacto eliminado');
      fetchData();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  if (loading) return <div className="p-8">Cargando...</div>;

  return (
    <div className="p-6 md:p-12 bg-secondary min-h-screen" data-testid="manage-country-contacts-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold mb-2">
              <Globe className="w-3.5 h-3.5" /> CONTACTOS
            </div>
            <h1 className="text-3xl font-heading font-bold text-primary">Contactos por País</h1>
            <p className="text-muted-foreground">Gestiona los responsables y datos de contacto de cada región.</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-contact-btn"><Plus className="w-4 h-4 mr-2" />Nuevo Contacto</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? 'Editar Contacto' : 'Nuevo Contacto'}</DialogTitle>
                <DialogDescription>
                  Datos del responsable de IAASP Academy en este país. Visibles públicamente en /contact.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>País *</Label>
                    <Select value={form.country} onValueChange={v => setField('country', v)}>
                      <SelectTrigger data-testid="contact-country-select">
                        <SelectValue placeholder="Selecciona país..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Nombre *</Label>
                    <Input value={form.name} onChange={e => setField('name', e.target.value)} required
                      data-testid="contact-name-input" placeholder="Nombre completo" />
                  </div>
                </div>
                <div>
                  <Label>Cargo / Posición</Label>
                  <Input value={form.position} onChange={e => setField('position', e.target.value)}
                    placeholder="Director Académico Regional" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={form.email} onChange={e => setField('email', e.target.value)}
                      placeholder="contacto@iaaspacademy.com" />
                  </div>
                  <div>
                    <Label>Teléfono</Label>
                    <Input value={form.phone} onChange={e => setField('phone', e.target.value)}
                      placeholder="+506 2000-0000" />
                  </div>
                </div>
                <div>
                  <Label>WhatsApp (con código de país)</Label>
                  <Input value={form.whatsapp} onChange={e => setField('whatsapp', e.target.value)}
                    placeholder="+506 8000-0000" data-testid="contact-whatsapp-input" />
                </div>
                <div>
                  <Label>Dirección</Label>
                  <Input value={form.address} onChange={e => setField('address', e.target.value)}
                    placeholder="Ciudad, país" />
                </div>
                <div>
                  <Label>Foto / Avatar</Label>
                  <div onClick={() => avatarRef.current?.click()}
                    className="mt-1 border-2 border-dashed rounded-xl p-3 cursor-pointer hover:border-primary transition-colors flex items-center gap-3">
                    {avatarPreview ? (
                      <img src={avatarPreview} alt="avatar" className="w-16 h-16 rounded-full object-cover" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                        <ImageIcon className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 text-sm text-muted-foreground">
                      <p>Clic para subir foto</p>
                      <p className="text-[11px]">JPG/PNG/WEBP · auto-redimensionada a 512×512</p>
                    </div>
                    <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarPick} />
                  </div>
                </div>
                <div>
                  <Label>Notas (horario, idioma, etc.)</Label>
                  <Textarea value={form.notes} onChange={e => setField('notes', e.target.value)} rows={2}
                    placeholder="Lunes a Viernes 8:00 - 17:00" />
                </div>
                <div>
                  <Label>Orden de aparición</Label>
                  <Input type="number" value={form.order} onChange={e => setField('order', e.target.value)}
                    placeholder="0" className="max-w-[120px]" />
                </div>
                <div className="flex gap-2 pt-2 border-t">
                  <Button type="submit" disabled={saving} data-testid="save-contact-btn">
                    {saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear')} Contacto
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Grouped list */}
        {Object.keys(grouped).length === 0 ? (
          <Card><CardContent className="py-16 text-center text-muted-foreground">
            <Globe className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Aún no hay contactos. ¡Crea el primero!
          </CardContent></Card>
        ) : (
          Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([country, list]) => (
            <div key={country} className="mb-6">
              <h2 className="text-lg font-heading font-bold text-primary mb-3 inline-flex items-center gap-2">
                <MapPin className="w-5 h-5 text-accent" />{country}
                <span className="text-xs font-normal text-muted-foreground">· {list.length}</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {list.map(c => (
                  <Card key={c.id} data-testid={`contact-card-${c.id}`}>
                    <CardContent className="p-4">
                      <div className="flex gap-3">
                        {c.avatar_url ? (
                          <img src={c.avatar_url.startsWith('/api') ? `${API_BASE}${c.avatar_url}` : c.avatar_url}
                            alt={c.name} className="w-14 h-14 rounded-full object-cover shrink-0" />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center shrink-0">
                            <UserIcon className="w-6 h-6 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{c.name}</p>
                          {c.position && <p className="text-xs text-muted-foreground truncate">{c.position}</p>}
                          <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                            {c.email && <p className="truncate"><Mail className="w-3 h-3 inline mr-1" />{c.email}</p>}
                            {c.whatsapp && <p className="truncate"><Phone className="w-3 h-3 inline mr-1" />{c.whatsapp}</p>}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <Button size="sm" variant="outline" onClick={() => handleEdit(c)} data-testid={`edit-contact-${c.id}`}>
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDelete(c.id)} data-testid={`delete-contact-${c.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ManageCountryContacts;
